import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api, { setLogoutCallback } from "../utils/api";
import { useLanguage } from "./LanguageContext";

// Create auth context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Add social links state
  const [socialLinks, setSocialLinks] = useState(null);

  // Add singleBooking state - initialize from localStorage if available
  const [singleBooking, setSingleBooking] = useState(() => {
    const stored = localStorage.getItem("singleBooking");
    try {
      return stored ? JSON.parse(stored) : false;
    } catch (error) {
      console.error("Error parsing singleBooking from localStorage:", error);
      localStorage.removeItem("singleBooking");
      return false;
    }
  });

  // Logout function - enhanced to handle automatic and manual logout
  const logout = useCallback(
    async (isAutomatic = false) => {
      try {


        // Clear localStorage and state
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("singleBooking");
        localStorage.removeItem("socialLinks");

        // Clear client-side cookies
        document.cookie.split(";").forEach((cookie) => {
          const cookieName = cookie.split("=")[0].trim();
          // Clear for all possible paths and domains
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.houfalmanazel.com;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=dev.houfalmanazel.com;`;
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        });

        // Clear authorization header
        delete api.defaults.headers.common["Authorization"];

        // Reset state
        setToken(null);
        setUser(null);
        setSingleBooking(false);
        setSocialLinks(null);

        // Navigate to home page
        navigate("/");

        // Show appropriate message
        if (isAutomatic) {
          toast.warning(
            t("sessionExpired") ||
              "Your session has expired. Please login again"
          );
        } else {
          toast.info(t("logoutMessage") || "You have been logged out");
        }
      } catch (error) {
        console.error("Error during logout:", error);
        // Force page reload as fallback
        window.location.href = "/";
      }
    },
    [navigate, t]
  );

  // Set up logout callback for API instance
  useEffect(() => {
    setLogoutCallback(logout);

    // Cleanup function
    return () => {
      setLogoutCallback(null);
    };
  }, [logout]);

  // Set token in Authorization header for API requests
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (token) {
          api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          // Load user from localStorage if available
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              setUser(parsedUser);
            } catch (error) {
              console.error("Error parsing stored user data:", error);
              localStorage.removeItem("user");
              localStorage.removeItem("token");
              setToken(null);
            }
          }

          // Load social links from localStorage if available
          const storedLinks = localStorage.getItem("socialLinks");
          if (storedLinks) {
            try {
              const parsedLinks = JSON.parse(storedLinks);
              setSocialLinks(parsedLinks);
            } catch (error) {
              console.error("Error parsing stored social links data:", error);
              localStorage.removeItem("socialLinks");
            }
          }
        } else {
          delete api.defaults.headers.common["Authorization"];
          setUser(null);
          setSocialLinks(null);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        // Clear everything on error
        localStorage.clear();
        setToken(null);
        setUser(null);
        setSocialLinks(null);
        setSingleBooking(false);
      }

      // Mark initialization as complete
      setInitializing(false);
    };

    initializeAuth();
  }, [token]);

  // Login function
  const login = async (credentials) => {
    try {
      setLoading(true);

      let responseData;

      // Check if we're being passed a social login response directly
      if (credentials._socialLoginResponse) {
        // Use the social login response data directly
        responseData = credentials._socialLoginResponse;
      }
      // Check if we're being passed a verification response directly
      else if (credentials._verificationResponse) {
        // Use the verification response data directly
        responseData = credentials._verificationResponse;
      } else {
        // Otherwise, make the normal login API call
        const response = await api.post("/auth/users/login", credentials);
        responseData = response.data;
      }

      // Extract token and user data from the response
      // Handle multiple response structures:
      // Social/verifyCookie: { status, token, user }
      // Regular login: { status, token, data }
      // Verification: { status, token, data }
      let newToken;
      let userData;

      if (credentials._socialLoginResponse || credentials._verificationResponse) {
        // Social login and verification responses have token and user at root level
        newToken = responseData.token;
        userData = responseData.user || responseData.data;
      } else {
        // Regular login has token and data
        newToken = responseData.token;
        userData = responseData.data;
      }

      // Validate response structure
      if (!newToken || !userData) {
        console.error("Response data:", responseData);
        throw new Error("Invalid response format: missing token or user data");
      }

      // Save to localStorage
      localStorage.setItem("token", newToken);
      localStorage.setItem("user", JSON.stringify(userData));

      // Update state
      setToken(newToken);
      setUser(userData);

      // Set authorization header
      api.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;

      // Only show toast if this is a normal login (not from verification or social)
      if (!credentials._verificationResponse && !credentials._socialLoginResponse) {
        toast.success(t("loginSuccess") || "Login successful");
      }

      return true;
    } catch (error) {
      console.error("Login error:", error);

      // Clear any stored data on login failure
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setToken(null);
      setUser(null);
      delete api.defaults.headers.common["Authorization"];

      // Don't show toast if it's already been handled by the interceptor
      if (!error.response || error.response.status !== 401) {
        const message =
          error.message ||
          error.response?.data?.message ||
          t("loginError") ||
          "Login failed";
        toast.error(message);
      }

      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update user data in context and localStorage
  const updateUserData = (updatedUserData) => {
    try {
      if (!user) {
        console.warn("Cannot update user data: no user logged in");
        return false;
      }

      // Create new user object with updated data
      const updatedUser = { ...user, ...updatedUserData };

      // Update localStorage
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Update state
      setUser(updatedUser);

      return true;
    } catch (error) {
      console.error("Error updating user data:", error);
      toast.error("Failed to update user data");
      return false;
    }
  };

  // Update password and log out
  const updatePassword = async (passwordData) => {
    try {
      setLoading(true);
      const response = await api.patch("/auth/updatePassword", passwordData);

      if (response.data.status === "success") {
        toast.success("تم تحديث كلمة المرور بنجاح. الرجاء إعادة تسجيل الدخول.");

        // Log out user after password change
        setTimeout(() => {
          logout(false); // Manual logout
        }, 1500); // Small delay to allow the toast to be seen

        return true;
      }
      return false;
    } catch (error) {
      console.error("Password update error:", error);

      // Don't show toast if it's already been handled by the interceptor
      if (!error.response) {
        const message = error.message || "فشل تحديث كلمة المرور";
        toast.error(message);
      }

      return false;
    } finally {
      setLoading(false);
    }
  };

  // Check if user is verified
  const isUserVerified = () => {
    return user?.isActive === true;
  };

  // Add a function to update social links
  const updateSocialLinks = (links) => {
    try {
      if (!links) {
        console.warn("Cannot update social links: no links provided");
        return false;
      }

      // Save links to localStorage
      localStorage.setItem("socialLinks", JSON.stringify(links));

      // Update state
      setSocialLinks(links);

      return true;
    } catch (error) {
      console.error("Error updating social links:", error);
      toast.error("Failed to update social links");
      return false;
    }
  };

  // Add function to update singleBooking
  const updateSingleBooking = (value) => {
    try {
      // Save to localStorage
      localStorage.setItem("singleBooking", JSON.stringify(value));

      // Update state
      setSingleBooking(value);

      return true;
    } catch (error) {
      console.error("Error updating single booking:", error);
      toast.error("Failed to update booking setting");
      return false;
    }
  };

  // Check if user is authenticated and token exists
  const isAuthenticated = Boolean(token && user);

  // Context value
  const contextValue = {
    user,
    token,
    loading,
    initializing,
    isAuthenticated,
    isVerified: isUserVerified(),
    login,
    logout: () => logout(false), // Expose manual logout
    updateUserData,
    updatePassword,
    socialLinks,
    updateSocialLinks,
    singleBooking,
    updateSingleBooking,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};